import Link from "next/link";
import { NOTE_URL, SNS_LINKS } from "@/lib/snsLinks";

interface Props {
  /**
   * 大学・キャリアセンター向けLPへの導線を表示するか。
   * 消費者サイト各ページでは true（既定）、LP自身では自己リンクになるため false を渡す。
   */
  showCareerCenterLink?: boolean;
  /**
   * MENREN FOR BUSINESS（企業向け一次面接）LPへの導線を表示するか。
   * 消費者サイト各ページでは true（既定）、B2B LP各ページでは false を渡す。
   */
  showBusinessLink?: boolean;
}

interface FooterLink {
  label: string;
  href: string;
  /** 外部サイトへのリンク。別タブで開く。 */
  external?: boolean;
  /** 表示可否を Props のフラグで切り替えるリンク。 */
  visibleWhen?: "careerCenter" | "business";
}

interface FooterGroup {
  heading: string;
  links: FooterLink[];
}

/**
 * フッターのメニュー。PC では見出しごとに列を分けて右側に並べ、
 * モバイルでは見出しを隠して1本のリストとして縦に積む。
 */
const FOOTER_GROUPS: FooterGroup[] = [
  {
    heading: "サービス",
    links: [
      { label: "新規登録", href: "/signup" },
      { label: "使い方", href: "/how-to-use" },
      { label: "ナレッジ記事", href: "/guides" },
    ],
  },
  {
    heading: "法人・教育機関の方へ",
    links: [
      {
        label: "大学・キャリアセンターの方へ",
        href: "/career-center",
        visibleWhen: "careerCenter",
      },
      {
        label: "企業の採用担当者の方へ",
        href: "/recruit",
        visibleWhen: "business",
      },
    ],
  },
  {
    heading: "サポート",
    links: [
      { label: "お問い合わせ", href: "/contact" },
      { label: "利用規約", href: "/agreement" },
      { label: "プライバシーポリシー", href: "/privacy" },
    ],
  },
  {
    heading: "MENRENについて",
    links: [
      { label: "運営会社", href: "https://www.x-hack.jp/", external: true },
      { label: "公式note", href: NOTE_URL, external: true },
    ],
  },
];

const LINK_CLASS = "text-white text-sm hover:text-primary transition-colors";

function FooterMenuLink({ label, href, external }: FooterLink) {
  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLASS}
      >
        {label}
      </a>
    );
  }
  return (
    <Link href={href} className={LINK_CLASS}>
      {label}
    </Link>
  );
}

export default function TopFooter({
  showCareerCenterLink = true,
  showBusinessLink = true,
}: Props) {
  // 両方のフラグが false の列は見出しだけが残らないよう、空になった列そのものを落とす。
  const groups = FOOTER_GROUPS.map((group) => ({
    ...group,
    links: group.links.filter((link) => {
      if (link.visibleWhen === "careerCenter") return showCareerCenterLink;
      if (link.visibleWhen === "business") return showBusinessLink;
      return true;
    }),
  })).filter((group) => group.links.length > 0);

  return (
    <footer id="top-footer" className="w-full bg-text p-6">
      <div className="mx-auto max-w-7xl md:px-6">
        {/* ロゴ（左端）とメニュー（右側） */}
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-10">
          {/* mx-auto: モバイルは中央寄せ。md 以降は左端に置き、サイズも一段上げる。
              mx-auto を外すと flex の stretch で img の箱が全幅に伸びるため明示する。 */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo_menren_white.svg"
            alt="MENREN"
            className="mx-auto h-10 shrink-0 md:mx-0 md:h-14"
          />

          <nav className="flex flex-col items-start gap-4 md:flex-row md:gap-10 lg:gap-14">
            {groups.map((group) => (
              <div
                key={group.heading}
                className="flex flex-col items-start gap-4 md:gap-3"
              >
                <p className="hidden text-xs font-bold text-white/70 md:block">
                  {group.heading}
                </p>
                {group.links.map((link) => (
                  <FooterMenuLink key={link.label} {...link} />
                ))}
              </div>
            ))}
          </nav>
        </div>

        {/* SNS: 区切り線を挟んで下段。PC では右端に寄せる */}
        <div className="mt-6 flex justify-center border-t border-white/15 pt-6 md:mt-10 md:justify-end">
          <div className="flex flex-row items-center gap-4">
            {SNS_LINKS.map((sns) => (
              <a
                key={sns.label}
                href={sns.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-primary transition-colors p-2 -m-2"
                aria-label={sns.label}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d={sns.svgPath} />
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
